import { he } from 'date-fns/locale';
import React, { useState } from 'react';
import { View, TouchableOpacity, Text, StyleSheet } from 'react-native';
import { CreateResponsiveStyle, DEVICE_SIZES, useDeviceSize } from 'rn-responsive-styles';


const VirtualKeyboard = ({ activeField, setInputValue, onClose }) => {
    const [isShiftActive, setIsShiftActive] = useState(false);
    const [currentKeyboardType, setCurrentKeyboardType] = useState('alphanumeric'); // Default to alphanumeric
    const [isSymbolsActive, setIsSymbolsActive] = useState(false); // Toggle for symbols
    const styles = useStyles();
    const handleKeyPress = (key) => {
        if (!activeField) return;

        if (key === '⏎') {
            onClose();
            return;
        }

        setInputValue(prev => {
            if (key === ' ' && prev[prev.length - 1] === ' ') {
                return prev; // Prevent consecutive spaces
            }
            return prev + key;
        });
    };

    const handleDelete = () => {
        if (!activeField) return;
        setInputValue(prev => prev.slice(0, -1)); // Remove the last character
    };

    const toggleShift = () => setIsShiftActive(prev => !prev);

    const toggleSymbols = () => {
        setIsSymbolsActive(prev => !prev);
        setCurrentKeyboardType('symbols'); // Force symbols mode when activated
    };

    const toggleKeyboardType = () => {
        if (isSymbolsActive) {
            setIsSymbolsActive(false); // Turn off symbols when switching to numbers/letters
            setCurrentKeyboardType('alphanumeric');
        } else {
            setCurrentKeyboardType(prev => (prev === 'numeric' ? 'alphanumeric' : 'numeric'));
        }
    };

    const keys = {
        numeric: [
            ['1', '2', '3'],
            ['4', '5', '6'],
            ['7', '8', '9'],
            ['ABC', '0', '⌫', '⏎']
        ],
        alphanumeric: [
            ['@','1', '2', '3', '4', '5', '6', '7', '8', '9','0','$'],
            ['q', 'w', 'e', 'r', 't', 'y', 'u', 'i', 'o', 'p'],
            ['a', 's', 'd', 'f', 'g', 'h', 'j', 'k', 'l'],
            ['⇧', 'z', 'x', 'c', 'v', 'b', 'n', 'm', '⌫'],
            ['123', ' ', '⏎', '#+=']
        ],
        uppercase: [
            ['@','1', '2', '3', '4', '5', '6', '7', '8', '9','0','$'],
            ['Q', 'W', 'E', 'R', 'T', 'Y', 'U', 'I', 'O', 'P'],
            ['A', 'S', 'D', 'F', 'G', 'H', 'J', 'K', 'L'],
            ['⇧', 'Z', 'X', 'C', 'V', 'B', 'N', 'M', '⌫'],
            ['123', ' ', '⏎', '#+=']
        ],
        symbols: [
            ['!', '@', '#', '$', '%', '^', '&', '*', '(', ')'],
            ['-', '_', '=', '+', '[', ']', '{', '}', '|', '\\'],
            [';', ':', '\'', '"', ',', '.', '/', '?', '⌫'],
            ['ABC', '`', '~', '<', '>', ' ', '⏎']
        ]
    };

    let activeKeys;
    if (isSymbolsActive) {
        activeKeys = keys.symbols;
    } else if (currentKeyboardType === 'numeric') {
        activeKeys = keys.numeric;
    } else {
        activeKeys = isShiftActive ? keys.uppercase : keys.alphanumeric;
    }

    return (
        <View style={styles.keyboardContainer}>
            {activeKeys.map((row, rowIndex) => (
                <View key={rowIndex} style={styles.row}>
                    {row.map((key) => (
                        <TouchableOpacity
                            key={key}
                            style={[
                                styles.key,
                                key === ' ' ? styles.spaceKey : {},
                                key === '⏎' ? styles.enterKey : {},
                                key === '⌫' ? styles.deleteKey : {},
                                key === '⇧' ? styles.shiftKey : {},
                                key === '#+=' || key === '123' || key === 'ABC' ? styles.toggleKey : {}
                            ]}
                            onPress={() => {
                                if (key === '⌫') {
                                    handleDelete();
                                } else if (key === '⏎') {
                                    onClose();
                                } else if (key === '⇧') {
                                    toggleShift();
                                } else if (key === '123') {
                                    toggleKeyboardType();
                                } else if (key === '#+=') {
                                    toggleSymbols();
                                } else if (key === 'ABC') {
                                    setIsSymbolsActive(false);
                                    setCurrentKeyboardType('alphanumeric');
                                } else {
                                    handleKeyPress(key);
                                }
                            }}
                        >
                            <Text style={styles.keyText}>{key === ' ' ? '␣' : key}</Text>
                        </TouchableOpacity>
                    ))}
                </View>
            ))}
        </View>
    );
};

const useStyles = CreateResponsiveStyle({
    keyboardContainer: {
        backgroundColor: '#f0f0f0',
        alignContent: 'center',
        justifyContent: 'center',
        alignSelf: 'center',
        borderRadius: 5,
        padding: '7.38%',
        width: "90%",
    },
    row: {
        flexDirection: 'row',
        justifyContent: 'center',
        marginBottom: 3
    },
    key: {
        padding: 5,
        margin: 2,
        minWidth: 50,
        minHeight: 50,
        borderRadius: 5,
        backgroundColor: '#e9eef3',
        alignItems: 'center',
        justifyContent: 'center',
        borderWidth: 1,
        borderColor: '#000'
    },
    keyText: {
        fontSize: 12,
        color: '#000'
    },
    spaceKey: {
        flex: 2,
        minWidth: 100,
        maxWidth: 600
    },
    enterKey: {
        backgroundColor: '#e9eef3',
        flex:.2,
    },
    deleteKey: {
        backgroundColor: '#e9eef3',
        flex:.2,
    },
    shiftKey: {
        backgroundColor: '#e9eef3',
        flex:.3,
    },
    toggleKey: {
        backgroundColor: '#e9eef3'
    }

},
{ [DEVICE_SIZES.MD]: {
            keyboardContainer: {
                backgroundColor: '#f0f0f0',
                paddingVertical: 5,
                alignContent: 'center',
                justifyContent: 'center',
                alignSelf: 'center',
                marginVertical: 10,
                borderRadius: 5,
                padding: 5,
                paddingVertical: 15,
                 },
            row: {
                flexDirection: 'row',
                justifyContent: 'center',
                marginBottom: 3
            },
            key: {
                padding: 5,
                margin: 2,
                minWidth: 35,
                minHeight: 35,
                borderRadius: 5,
                backgroundColor: '#e9eef3',
                alignItems: 'center',
                justifyContent: 'center'
            },
            enterKey: {
                backgroundColor: '#e9eef3',
            },
            deleteKey: {
                backgroundColor: '#e9eef3',
            },
            shiftKey: {
                backgroundColor: '#e9eef3',
            },
            toggleKey: {
                backgroundColor: '#e9eef3'
            }

        },
        [DEVICE_SIZES.SM]: {
            keyboardContainer: {
                backgroundColor: '#f0f0f0',
                paddingVertical: 5,
                alignContent: 'center',
                justifyContent: 'center',
                alignSelf: 'center',
                marginVertical: 10,
                borderRadius: 5,
                padding: 5,
                paddingVertical: 15,
                
            },
            row: {
                flexDirection: 'row',
                justifyContent: 'center',
                marginBottom: 3
            },
            key: {
                padding: 5,
                margin: 2,
                minWidth: 20,
                minHeight: 30,
                borderRadius: 5,
                backgroundColor: '#e9eef3',
                alignItems: 'center',
                justifyContent: 'center'
            }, enterKey: {
                backgroundColor: '#e9eef3',
            },
            deleteKey: {
                backgroundColor: '#e9eef3',
            },
            shiftKey: {
                backgroundColor: '#e9eef3',
            },
            toggleKey: {
                backgroundColor: '#e9eef3'
            }
 },
        [DEVICE_SIZES.XS]: {
            keyboardContainer: {
                backgroundColor: '#f0f0f0',
                paddingVertical: 5,
                alignContent: 'center',
                justifyContent: 'center',
                alignSelf: 'center',
                marginVertical: 10,
                borderRadius: 5,
                padding: 5,
                paddingVertical: 15,

                marginVertical: 50,
                
            },
            row: {
                flexDirection: 'row',
                justifyContent: 'center',
                marginBottom: 3
            },
            key: {
                padding: 5,
                margin: 2,
                minWidth: 20,
                minHeight: 20,
                borderRadius: 5,
                backgroundColor: '#e9eef3',
                alignItems: 'center',
                justifyContent: 'center'
            },
            enterKey: {
                backgroundColor: '#e9eef3',
            },
            deleteKey: {
                backgroundColor: '#e9eef3',
            },
            shiftKey: {
                backgroundColor: '#e9eef3',
            },
            toggleKey: {
                backgroundColor: '#e9eef3'
            }
        },
    });

export default VirtualKeyboard;
